import React, { useEffect } from "react";

import DownloadIcon from '@mui/icons-material/Download';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";

import FormModifierWrapper from "@refere/ui-utils/ui/components/FormModifierWrapper";
import ModalDialog, { ModalDialogHeader } from "@refere/ui-utils/ui/components/ModalDialog";

import { useHookstate } from "@hookstate/core";
import { object, string } from 'yup';
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';

import { mostraDeDateISOparaDataPtBr } from "@refere/ui-utils/ui/utils/format";

import { globalState } from "../../../../features/global-state";

import { cancelar, downloadFormCancelamento, downloadFormCancelamentoAnexado } from "../../../../features/pages/certificado/certificado";

const schema = object({
    codMotivoCancelamento: string().nullable()
        .required("Motivo de Cancelamento é obrigatório")
        .test("is-valid", 
            "Este Motivo de Cancelamento exige o Upload do Formulário de Cancelamento", 
            (_, { parent }) => {
                if (parent.usarFormCancelamento)
                    return !!parent.nomeArquivoFormCancelamento;
                else 
                    return true;
                            
            }) 
});

interface ICancelamentoForm {
    codMotivoCancelamento: string
    nomeArquivoFormCancelamento: string
    usarFormCancelamento: boolean
    uploadFormCancelamento?: File
}

const ParteCancelamento = () => {
    const certificadoState = useHookstate(globalState.pageCertificado);

    const modalConfirmaCancelamentoIsOpen = useHookstate(false);

    const { control, handleSubmit, watch, setValue, getValues, trigger, formState } = useForm<ICancelamentoForm>({
        mode: "onChange",
        resolver: yupResolver(schema as any)
    });

    const motivosCancelamento = Array.from(certificadoState?.motivosCancelamento?.get({noproxy: true}) ?? []);
    const foiFeitoUpdloadFormCancelamento = !!certificadoState?.nomeArquivoFormCancelamento?.get();

    const cancelado = !!certificadoState?.dtHrCancelamento?.get();

    useEffect(() => {
        setValue("codMotivoCancelamento", certificadoState?.codMotivoCancelamento?.get());
        setValue("nomeArquivoFormCancelamento", certificadoState?.nomeArquivoFormCancelamento?.get());
        setValue("uploadFormCancelamento", null);
    }, [ certificadoState ]);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        (
            async () => {
                const codMotCancelamento = getValues("codMotivoCancelamento");
                if (codMotCancelamento) {
                    const usarFormCancelamento = !!motivosCancelamento
                        .find(mc => mc.codMotivo === codMotCancelamento)
                        ?.usarFormCancelamento;
                    setValue("usarFormCancelamento", usarFormCancelamento);
                    await trigger("codMotivoCancelamento");
                }
            }
        )();
    }, [ watch("codMotivoCancelamento") ]);

    return (
        <>
        {
            certificadoState.dtHrEmissaoCertificado?.get() &&
            <>
                <Box>
                    <Typography pl={2} fontSize="16px" mt={2}>
                        Cancelamento
                    </Typography>
                    <Divider />
                </Box>
                {
                    !cancelado &&
                    <Box mt={2} pl={2} display="flex" flexDirection="column" >
                        <FormModifierWrapper 
                            name="codMotivoCancelamento"
                            control={control}
                            render={({ field }) => 
                                <>
                                    <FormLabel component="legend" sx={{ fontSize:"14px" }} focused={false}>
                                        Motivos de Cancelamento
                                    </FormLabel> 
                                    <RadioGroup
                                        row={false}
                                        onChange={field.onChange}
                                    >
                                        {motivosCancelamento.map((motivo) => (
                                            <FormControlLabel
                                                key={motivo.codMotivo} 
                                                value={motivo.codMotivo} 
                                                control={<Radio sx={{padding: "6px"}} size="small"/>} 
                                                label={motivo.descricao}  
                                                sx={{ 
                                                    ".MuiFormControlLabel-label": {
                                                        padding: "1px 0"
                                                    }                 
                                                }}
                                            />                    
                                        ))}
                                    </RadioGroup>
                                </>
                            }
                        />
                    </Box>
                }                    
                {
                    watch("usarFormCancelamento") &&
                    <Box display="flex" alignItems="center" flexWrap="wrap" pl={2} mt={1} mb={1} >
                        <Button 
                            sx={{
                                textTransform: "none",
                                mr: 1,
                                mt: 1
                            }}
                            size="small"
                            disabled={cancelado}
                            onClick={() => downloadFormCancelamento(watch("codMotivoCancelamento"))}
                            variant="contained">
                            Download formulário
                        </Button>
                        <Box display="flex" alignItems="center" flexWrap="wrap" mt={1} >
                            <Button 
                                sx={{
                                    textTransform: "none",
                                    mr: 1
                                }}
                                size="small"
                                disabled={cancelado}
                                component="label"
                                variant="contained">                                    
                                    Upload formulário
                                    <input
                                        type="file"
                                        id="upload-form-cancelamento-file"
                                        accept="application/pdf"
                                        hidden    
                                        onChange={async ({ target }) => {
                                            setValue("nomeArquivoFormCancelamento", target.files[0].name);
                                            setValue("uploadFormCancelamento", target.files[0]);
                                            await trigger("codMotivoCancelamento");
                                        }}
                                    />
                            </Button>
                            { 
                                watch("nomeArquivoFormCancelamento") &&
                                <Typography variant="caption">
                                    {watch("nomeArquivoFormCancelamento")}
                                    {
                                        foiFeitoUpdloadFormCancelamento && 
                                        <IconButton onClick={() => downloadFormCancelamentoAnexado()}>
                                            <DownloadIcon />
                                        </IconButton>
                                    }
                                </Typography>                        
                            }
                        </Box>
                    </Box>
                }
                <Box mt={2} pl={2}>
                    <Button 
                        sx={{
                            textTransform: "none",
                            minWidth: "210px"
                        }}
                        disabled={!formState.isValid || cancelado}
                        type="submit"
                        onClick={async () => {
                            await handleSubmit(async () => {
                                if (!getValues("usarFormCancelamento")) 
                                    modalConfirmaCancelamentoIsOpen.set(true);
                                
                                else 
                                    await cancelar(watch("codMotivoCancelamento"), watch("uploadFormCancelamento"));
                            })();
                        }}
                        variant="contained">
                        Cancelar o seguro
                    </Button>
                    <ModalDialog 
                        onClose={() => modalConfirmaCancelamentoIsOpen.set(false)} 
                        isOpen={modalConfirmaCancelamentoIsOpen.get()}
                        sx={{
                            ".MuiPaper-root": {
                                maxWidth: "none",
                                width: "735px"
                            }
                        }} >
                        <ModalDialogHeader
                            onClose={() => modalConfirmaCancelamentoIsOpen.set(false)} >
                            <Box minWidth="200px">
                                Cancelamento
                            </Box>
                        </ModalDialogHeader>
                        <DialogContent dividers>
                            <p style={{ marginTop: 0}}>
                            Você está solicitando o cancelamento do seguro Movimentação Financeira- AILOS.<br />
                            Tem certeza que deseja continuar?
                            </p>
                        </DialogContent> 
                        <DialogActions>
                            <Button 
                                variant='outlined' 
                                sx={{ textTransform: "none" }} 
                                onClick={() => modalConfirmaCancelamentoIsOpen.set(false)} >
                                Fechar
                            </Button>
                            <Button 
                                variant="contained" 
                                sx={{ textTransform: "none" }} 
                                onClick={async () => {
                                    await cancelar(watch("codMotivoCancelamento"), null);
                                    modalConfirmaCancelamentoIsOpen.set(false);
                                }} >
                                Confirmo o cancelamento
                            </Button>
                        </DialogActions>
                    </ModalDialog>
                </Box>
                <Box pl={2} mt={1}>
                {
                    cancelado &&
                    <Typography variant="caption" mt={4}>
                        <span>Status Cancelamento: cancelado em </span>
                        {
                            mostraDeDateISOparaDataPtBr(certificadoState.dtHrCancelamento.get({noproxy: true}), "dd/MM/yyyy HH:mm:ss")
                        }, Motivo: {(() => {
                            const motivoCancelamento = motivosCancelamento.find(mc => mc.codMotivo === certificadoState.codMotivoCancelamento.get());
                            return `${motivoCancelamento.codMotivo} - ${motivoCancelamento.descricao}`;
                            })()}
                    </Typography>
                }
                </Box>
            </>
        }  
        </>
    );
};

export default ParteCancelamento;